/**
 * Response Adapter for Edge Function envelope format
 * Handles the transition between direct API responses and Edge Function wrapped responses
 */

import { EdgeFunctionResponse } from "../core/types.js";
import { ApiResponse } from "./http-client.js";

export interface UsageInfo {
  tokens_used: number;
  cost_usd: number;
  cached: boolean;
}

export interface TierInfo {
  tier: string;
  usage_remaining: number;
}

export interface UnwrappedResponse<T> {
  data?: T;
  error?: {
    message: string;
    code?: string;
  };
  usage?: UsageInfo;
  tier_info?: TierInfo;
  status: number;
}

/**
 * Adapts Edge Function envelope response to SDK internal format
 * Edge Functions return: { success, data, error, usage, tier_info }
 * SDK expects: { data, error, status }
 */
export function adaptEdgeFunctionResponse<T>(
  httpResponse: ApiResponse<EdgeFunctionResponse<T>>
): UnwrappedResponse<T> {
  // If HTTP request itself failed
  if (httpResponse.error) {
    return {
      status: httpResponse.status,
      error: httpResponse.error,
    };
  }

  const envelope = httpResponse.data;

  // If no envelope data (shouldn't happen)
  if (!envelope) {
    return {
      status: httpResponse.status,
      error: {
        message: "Empty response from server",
        code: "EMPTY_RESPONSE",
      },
    };
  }

  // If Edge Function returned an error
  if (!envelope.success && envelope.error) {
    return {
      status: httpResponse.status,
      error: envelope.error,
      usage: envelope.usage,
      tier_info: envelope.tier_info,
    };
  }

  // Successful response
  return {
    status: httpResponse.status,
    data: envelope.data,
    usage: envelope.usage,
    tier_info: envelope.tier_info,
  };
}

/**
 * Checks if a response appears to be an Edge Function envelope
 */
export function isEdgeFunctionEnvelope(data: unknown): data is EdgeFunctionResponse<unknown> {
  if (typeof data !== "object" || data === null) {
    return false;
  }

  const obj = data as Record<string, unknown>;
  return "success" in obj && typeof obj.success === "boolean";
}

/**
 * Smart adapter that detects response format and adapts accordingly
 * Supports both direct API responses and Edge Function envelopes
 */
export function adaptResponse<T>(
  httpResponse: ApiResponse<T | EdgeFunctionResponse<T>>
): UnwrappedResponse<T> {
  // If HTTP request itself failed
  if (httpResponse.error) {
    return {
      status: httpResponse.status,
      error: httpResponse.error,
    };
  }

  const responseData = httpResponse.data;

  // Check if it's an Edge Function envelope
  if (isEdgeFunctionEnvelope(responseData)) {
    return adaptEdgeFunctionResponse({
      status: httpResponse.status,
      data: responseData as EdgeFunctionResponse<T>,
    });
  }

  // Direct API response (legacy format)
  return {
    status: httpResponse.status,
    data: responseData as T,
  };
}

/**
 * Cache entry for offline fallback support
 */
export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  endpoint: string;
  params?: Record<string, unknown>;
}

/**
 * Simple in-memory cache for offline fallback
 */
export class ResponseCache {
  private cache: Map<string, CacheEntry<unknown>> = new Map();
  private ttl: number;

  constructor(ttlMs: number = 300000) { // Default 5 minutes
    this.ttl = ttlMs;
  }

  private generateKey(endpoint: string, params?: Record<string, unknown>): string {
    const paramStr = params ? JSON.stringify(params) : "";
    return `${endpoint}:${paramStr}`;
  }

  set<T>(endpoint: string, data: T, params?: Record<string, unknown>): void {
    const key = this.generateKey(endpoint, params);
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      endpoint,
      params,
    });
  }

  get<T>(endpoint: string, params?: Record<string, unknown>): T | null {
    const key = this.generateKey(endpoint, params);
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check if expired
    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  clear(): void {
    this.cache.clear();
  }

  /**
   * Clean up expired entries
   */
  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.ttl) {
        this.cache.delete(key);
      }
    }
  }
}
